import { Slugs } from 'common/urls/Slugs';
import { isNull } from 'lodash';
import { DateTime } from 'luxon';
import NoMatchCat from 'src/assets/svg/empty-state-cat.svg';
import PersonalShefBowl from 'src/assets/svg/personal-shef-bowl.svg';
import CalendarGreen from 'src/assets/svg/shef/calendar_green.svg';
import CalendarRed from 'src/assets/svg/shef/calendar_red.svg';
import { DishFirstSegmentFilter } from 'src/gqlReactTypings.generated.d';
import { PrimaryButton } from 'src/shared/design-system/Buttons';
import { Paragraph } from 'src/shared/design-system/Text';
import { useHistory } from 'src/shared/hooks/useHistory';
import { Routes } from 'src/shared/Routes';
import { getSluggedPath } from 'src/shared/utils/RouteUtilities';
import { useCachedZipCode } from 'src/user-preferences/useCachedZipCode';
import styled from 'styled-components';
import { Font, ScreenWidth, Spacing } from 'web-common/src/shared/styles';

const BannerContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 2rem;
  width: 100%;
`;

const SuccessBanner = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 16px;
  background: #f2fdfb;
  border: 1px solid #00af90;
  border-radius: 8px;
  margin: auto;
  max-width: 1200px;
  width: 100%;
  @media (min-width: ${ScreenWidth.SMALL}) {
    max-width: 724px;
  }
  @media (min-width: ${ScreenWidth.MEDIUM}) {
    max-width: 940px;
  }
`;

const FailureBanner = styled(SuccessBanner)`
  background: #fde9ec;
  border: 1px solid #ec2044;
`;

const TitleText = styled(Paragraph)`
  display: flex;
  align-items: center;
  gap: ${Spacing.SINGLE};
  font-weight: ${Font.Weight.MEDIUM};
  @media (max-width: ${ScreenWidth.SMALL}) {
    font-size: ${Font.Size.M};
  }
`;

const MainText = styled.h3`
  display: flex;
  align-items: center;
  gap: ${Spacing.SINGLE};
  text-align: center;
  font-weight: bold;
`;

const MainTextSmall = styled.h4`
  font-weight: bold;
`;

const SubtitleText = styled(Paragraph)`
  display: flex;
  align-items: center;
  gap: ${Spacing.SINGLE};
  font-size: ${Font.Size.S};
  @media (max-width: ${ScreenWidth.SMALL}) {
    font-size: ${Font.Size.XS};
  }
`;

const LinkText = styled(SubtitleText)`
  text-decoration: underline;
  cursor: pointer;
`;

const StyledLinkText = styled(LinkText)`
  margin-bottom: 0;
  margin-left: 15px;
`;

const BannerImage = styled.img`
  min-width: 100px;
  @media (max-width: ${ScreenWidth.SMALL}) {
    min-width: 70px;
  }
`;

const BannerButton = styled(PrimaryButton)`
  border-radius: 8px;
  padding: 8px 16px;
`;

interface BannerProps {
  nextDeliveryDate: DateTime;
  setDateToNextDeliveryDate: () => void;
}

export const SuccessChangeDeliveryDateBanner = ({ nextDeliveryDate, setDateToNextDeliveryDate }: BannerProps) => {
  const formattedNextDeliveryDate = nextDeliveryDate.toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY);
  return (
    <BannerContainer>
      <FailureBanner>
        <div>
          <TitleText>This shef isn't available on your selected delivery day</TitleText>
          <SubtitleText>{`Next available delivery day is ${formattedNextDeliveryDate}`}</SubtitleText>
          <LinkText onClick={setDateToNextDeliveryDate}>Change delivery day</LinkText>
        </div>
        <div>
          <BannerImage src={CalendarGreen} alt='Calendar' />
        </div>
      </FailureBanner>
    </BannerContainer>
  );
};

export const FailureChangeDeliveryDateBanner = ({ nextDeliveryDate, setDateToNextDeliveryDate }: BannerProps) => {
  const formattedNextDeliveryDate = nextDeliveryDate.toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY);
  return (
    <BannerContainer>
      <FailureBanner>
        <div>
          <TitleText>Items in your cart are scheduled for a different delivery date.</TitleText>
          <SubtitleText>{`Next available delivery day is ${formattedNextDeliveryDate}`}</SubtitleText>
          <LinkText onClick={setDateToNextDeliveryDate}>Change delivery day</LinkText>
        </div>
        <div>
          <BannerImage src={CalendarRed} alt='Calendar' />
        </div>
      </FailureBanner>
    </BannerContainer>
  );
};

interface UnavailableShefBannerProps {
  messageShef: () => void;
  shefAllowedToChat: boolean;
}

export const UnavailableShefBanner = ({ messageShef, shefAllowedToChat }: UnavailableShefBannerProps) => {
  const [zipCode] = useCachedZipCode();
  const history = useHistory();

  const seeRecommendedShefs = () => {
    if (zipCode) {
      const path = getSluggedPath(Routes.CONSUMER_EXPLORE, [{ slug: Slugs.ZIP_CODE, value: zipCode }], {
        c: DishFirstSegmentFilter.RecommendedShefs,
      });
      history.push(path);
    }
  };
  return (
    <BannerContainer>
      <FailureBanner>
        <div>
          <TitleText>Unfortunately, this shef is unavailable this week.</TitleText>
          <div style={{ display: 'flex' }}>
            <BannerButton onClick={seeRecommendedShefs}>Browse shefs</BannerButton>
            {shefAllowedToChat && <StyledLinkText onClick={messageShef}>Message shef</StyledLinkText>}
          </div>
        </div>
        <div>
          <BannerImage src={CalendarRed} alt='Calendar' />
        </div>
      </FailureBanner>
    </BannerContainer>
  );
};

interface Props {
  isShefAvailable: boolean;
  isCartEmpty: boolean;
  isShefAvailableInFuture: boolean;
  nextDeliveryDate: DateTime | null;
  messageShef: () => void;
  shefAllowedToChat: boolean;
  setDateToNextDeliveryDate: () => void;
}

const ConsumerOrderBanner = ({
  isShefAvailable,
  isCartEmpty,
  isShefAvailableInFuture,
  nextDeliveryDate,
  messageShef,
  shefAllowedToChat,
  setDateToNextDeliveryDate,
}: Props) => {
  if (!isShefAvailableInFuture || isNull(nextDeliveryDate)) {
    return <UnavailableShefBanner messageShef={messageShef} shefAllowedToChat={shefAllowedToChat} />;
  }

  if (!isCartEmpty && !isShefAvailable) {
    return (
      <FailureChangeDeliveryDateBanner
        nextDeliveryDate={nextDeliveryDate}
        setDateToNextDeliveryDate={setDateToNextDeliveryDate}
      />
    );
  }

  if (!isShefAvailable) {
    return (
      <SuccessChangeDeliveryDateBanner
        nextDeliveryDate={nextDeliveryDate}
        setDateToNextDeliveryDate={setDateToNextDeliveryDate}
      />
    );
  }
  return null;
};

export const NoPerfectMatchesBanner = ({ useSmallText = false }) => {
  const displayText = 'Although there are no perfect matches, discover great alternatives below!';
  const mainText = useSmallText ? <MainTextSmall>{displayText}</MainTextSmall> : <MainText>{displayText}</MainText>;

  return (
    <BannerContainer>
      <SuccessBanner>
        <div className={useSmallText ? 'flex items-center' : ''}>{mainText}</div>
        <div>
          <BannerImage src={PersonalShefBowl} />
        </div>
      </SuccessBanner>
    </BannerContainer>
  );
};

interface NoMatchBannerProps {
  onClick: () => void;
}

export const NoMatchesBanner = ({ onClick }: NoMatchBannerProps) => (
  <BannerContainer>
    <FailureBanner>
      <div>
        <MainText>Your pick sounds delicious, but no dishes match your preference</MainText>
        <StyledLinkText onClick={onClick}>Clear Filters</StyledLinkText>
      </div>
      <div>
        <BannerImage src={NoMatchCat} alt='Calendar' />
      </div>
    </FailureBanner>
  </BannerContainer>
);
export default ConsumerOrderBanner;
